import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  Stack,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip } from '../i18n/LocalizedMui';
import Typography from '../i18n/LocalizedTypography';
import { useTranslation } from 'react-i18next';
import { VedicYoga } from '../../hooks/useVedicYogas';

const strengthColor: Record<string, 'success' | 'warning' | 'default'> = {
  'Very Strong': 'success',
  Strong: 'success',
  Medium: 'warning',
  Weak: 'default',
};

interface VedicYogasPanelProps {
  yogas: VedicYoga[];
  loading?: boolean;
  error?: string | null;
}

const sectionOrder = ['Confirmed Yogas', 'Possible Yogas', 'Challenging Yogas / Arishtas'];

export default function VedicYogasPanel({ yogas = [], loading = false, error = null }: VedicYogasPanelProps) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);

  const slug = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const yogaText = (path: string, fallback: string) => t(`yogas.${path}`, { defaultValue: fallback });
  const translateList = (items: string[]) => items.map((item) => yogaText(`effects.${slug(item)}`, item));

  const grouped = useMemo(() => {
    return yogas.reduce<Record<string, VedicYoga[]>>((acc, yoga) => {
      const key = !yoga.isPositive
        ? 'Challenging Yogas / Arishtas'
        : yoga.status === 'Confirmed'
          ? 'Confirmed Yogas'
          : 'Possible Yogas';

      if (!acc[key]) acc[key] = [];
      acc[key].push(yoga);

      return acc;
    }, {});
  }, [yogas]);

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {t('yogas.title')}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('yogas.description')}
      </Typography>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2">{t('yogas.detecting')}</Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      {!loading && !yogas.length && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              {t('yogas.noneDetected')}
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading && sectionOrder.filter((groupName) => grouped[groupName]?.length).map((groupName) => (
        <Box key={groupName} sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
            {t(`yogas.sections.${slug(groupName)}`, { defaultValue: groupName })}
          </Typography>

          <Grid container spacing={2}>
            {grouped[groupName].map((yoga) => {
              const open = openId === yoga.id;

              return (
                <Grid size={{ xs: 12, md: 6 }} key={yoga.id}>
                  <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="h6" fontWeight={600}>
                          {yogaText(`names.${yoga.id}`, yoga.name)}
                        </Typography>

                        <Chip
                          size="small"
                          label={t(`yogas.strength.${slug(yoga.strength)}`, { defaultValue: yoga.strength })}
                          color={strengthColor[yoga.strength] || 'default'}
                        />

                        <Chip
                          size="small"
                          label={yoga.isPositive ? yogaText(`types.${slug(yoga.type || 'Positive Yoga')}`, yoga.type || 'Positive Yoga') : t('yogas.types.challenging')}
                          color={yoga.isPositive ? 'success' : 'error'}
                          variant="outlined"
                        />

                        <Chip size="small" label={t(`yogas.status.${slug(yoga.status)}`, { defaultValue: yoga.status })} variant="outlined" />
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {yogaText(`groups.${slug(yoga.group)}`, yoga.group)} • {yogaText(`categories.${slug(yoga.category)}`, yoga.category)}
                      </Typography>

                      {yoga.needsDashaConfirmation && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          {t('yogas.needsConfirmation')}
                        </Typography>
                      )}

                      <Button
                        size="small"
                        sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                        onClick={() => setOpenId(open ? null : yoga.id)}
                      >
                        {open ? t('yogas.hideDetails') : t('yogas.viewDetails')}
                      </Button>

                      <Collapse in={open}>
                        <Divider sx={{ my: 1.5 }} />

                        <Typography variant="subtitle2" fontWeight={600}>
                          {t('yogas.formation')}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {yogaText(`formations.${yoga.id}`, yoga.formation)}
                        </Typography>

                        {!!yoga.effects?.length && (
                          <>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {t('yogas.possibleEffects')}
                            </Typography>

                            <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                              {translateList(yoga.effects).map((effect, index) => (
                                <Typography component="li" variant="body2" key={`${yoga.id}-effect-${index}`}>
                                  {effect}
                                </Typography>
                              ))}
                            </Box>
                          </>
                        )}

                        {!!yoga.remedies?.length && (
                          <>
                            <Typography variant="subtitle2" fontWeight={600}>
                              {t('yogas.remedies')}
                            </Typography>

                            <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                              {yoga.remedies.map((remedy, index) => (
                                <Typography component="li" variant="body2" key={`${yoga.id}-remedy-${index}`}>
                                  {yogaText(`remedyText.${slug(remedy)}`, remedy)}
                                </Typography>
                              ))}
                            </Box>
                          </>
                        )}
                      </Collapse>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}
